import { EnvironmentOutlined, FullscreenOutlined, LoadingOutlined } from '@ant-design/icons';
import { type RadioChangeEvent } from 'antd5';
import { isFinite, orderBy } from 'lodash';
import queryString from 'query-string';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DamageTooltip } from 'components/damages/DamageTooltip';
import {
  CriticalLevel,
  SeverityAndCriticalIndicator,
} from 'components/SeverityAndCriticalIndicator';
import { Button, Spin, Spinner } from 'components/ui';
import { CompareDamagesIcon } from 'horizon/components/Damages/HorizonDamageImage/CompareDamagesIcon';
import {
  type ComparePicturesParams,
  type PreviousDamageInfo,
  type PreviousInspectionInfo,
} from 'horizon/components/Damages/HorizonDamageImage/types';
import {
  type BladeTarget,
  type RotationTarget,
  type TowerOrTransitionPieceTarget,
} from 'horizon/routes/Inspections/types';
import {
  type AtlasGqlHorizonDamageObservation,
  type AtlasGqlImageRgb,
  type AtlasGqlPicture,
} from 'types/atlas-graphql';
import { useAccountContext } from 'utils/account/AccountContext';
import { useResizeObserver } from 'utils/hooks';
import { IS_360_VIEWER } from 'utils/release-toggles';
import { Link, ModalLink, useHistory } from 'utils/router';

import { ThreeDimensionalIcon } from '../ImageViewer/Icons/ThreeDimensionalIcon';
import { Image } from '../ImageViewer/ImageOLD';
import { getRotationDegrees, isRotationTarget } from '../ImageViewer/utils';

import {
  AttributesContainer,
  AttributesContainerZoomable,
  ButtonContainer,
  ButtonGroup,
  CompareButton,
  DistanceTag,
  ExpandButton,
  ExtraContentContainer,
  LazyLoadContainer,
  MapButton,
  ReviewedToggle,
  RotationContainer,
  StyledPolygon,
  StyledSvg,
  ThumbnailContainer,
  ThumbnailImage,
} from './ImageThumbnail.style';

export type ImageThumbnailProps = {
  picture: Pick<
    AtlasGqlPicture,
    'id' | 'globalId' | 'filename' | 'homography' | 'image' | 'reviewed' | 'target'
  > & {
    imageWidth?: number;
    imageHeight?: number;
  };
  observations?: AtlasGqlHorizonDamageObservation[];
  allowZoom?: boolean;
  shouldRotate?: boolean;
  scaleFilter?: number;
  aspectRatio?: string;
  onToggleReviewed?: (
    picture: Pick<AtlasGqlPicture, 'id' | 'globalId' | 'reviewed'>,
    reviewed: boolean
  ) => Promise<void>;
  scrollParent?: Element | null;
  scrollParentSelector?: string;
  shouldShowReviewedToggle?: boolean;
  shouldShowSeverity?: boolean;
  shouldShowDistance?: boolean;
  shouldShowExpand?: boolean;
  expandToInspectionId?: string;
  primaryIndicator?: React.ReactNode;
  $isInCarousel?: boolean;
  shouldAutoFocus?: boolean;
  previousDamageInfo?: PreviousDamageInfo;
  prevInspectionDetails?: PreviousInspectionInfo;
  handleCompareNavigation?: ({
    previousDamageInfo,
    previousInspectionInfo,
  }: ComparePicturesParams) => Promise<void>;
  compareInspectionPicturesLoading?: boolean;
};

export const ImageThumbnail: React.FC<ImageThumbnailProps> = ({
  picture,
  observations,
  allowZoom = false,
  shouldRotate = false,
  scaleFilter,
  aspectRatio,
  onToggleReviewed = () => {},
  scrollParent,
  scrollParentSelector,
  shouldShowReviewedToggle = false,
  shouldShowSeverity,
  shouldShowDistance,
  shouldShowExpand,
  expandToInspectionId,
  primaryIndicator,
  $isInCarousel = false,
  shouldAutoFocus = true,
  previousDamageInfo,
  prevInspectionDetails,
  handleCompareNavigation,
  compareInspectionPicturesLoading,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { hasReleaseToggle } = useAccountContext();
  const has360ViewerReleaseToggle = hasReleaseToggle(IS_360_VIEWER);
  const [imageLoaded, setImageLoaded] = useState<boolean>(false);
  const [scale, setScale] = useState<number>(0);
  const [reviewedStatusLoading, setReviewedStatusLoading] = useState<boolean>(false);
  const imageRef = useRef<HTMLImageElement>(null);
  const imageRect: DOMRectReadOnly | undefined = useResizeObserver(imageRef);

  const { target, image, filename, homography, reviewed } = picture;
  const radialDistance = (target as BladeTarget)?.radialDistance;
  const height = (target as TowerOrTransitionPieceTarget)?.height;
  const { signedUrl, signedThumbnailUrl } = image as AtlasGqlImageRgb; // TODO: figure out the types here - does this work?
  const url = signedUrl?.url;
  const thumbnailUrl = signedThumbnailUrl?.url;

  const componentRotationDegrees = isRotationTarget(target)
    ? getRotationDegrees(target as RotationTarget)
    : 0;

  const nf = new (Intl.NumberFormat as any)(undefined, {
    maximumFractionDigits: 1,
    unit: 'meter',
    unitDisplay: 'narrow',
    style: 'unit',
  });

  useEffect(() => {
    setReviewedStatusLoading(false);
  }, [reviewed]);

  const handleLoad = (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
    setScale(event.currentTarget.height / event.currentTarget.width);
    setImageLoaded(true);
  };

  // The main intent here is to override any exif data that might
  // cause the image to rotate unnecesarily.
  // https://sharp.pixelplumbing.com/api-resize#resize
  const fullSizeImageWithoutEXIF = `${url}&fit=inside`;

  const highestSeverityObservation: AtlasGqlHorizonDamageObservation | undefined = orderBy(
    observations,
    ({ attrs }) => (attrs?.Severity ? Number(attrs.Severity) : 0),
    'desc'
  )[0];
  const highestSeverity: number | undefined = highestSeverityObservation?.attrs?.Severity;
  const isCritical: boolean = !!observations?.some(({ critical }) => !!critical);
  const picturePath: string = !!highestSeverityObservation
    ? `/pictures/${picture.id}?observation=${highestSeverityObservation.id}`
    : `/pictures/${picture.id}`;
  const mapPath = expandToInspectionId
    ? `/inspections/${expandToInspectionId}/map?${queryString.stringify({
        pictureId: picture.id,
        observationId: highestSeverityObservation?.id,
      })}`
    : undefined;
  const shouldExpandToInspectionMap = Boolean(expandToInspectionId);

  const handleClickObservation = (observationId: string) => {
    history.push(`/pictures/${picture.id}${observationId ? `?observation=${observationId}` : ''}`, {
      useModal: true,
      state: {
        fromThumbnail: true,
      },
    });
  };

  const is360Picture = useMemo(() => {
    const width = picture?.imageWidth ?? 1;
    const height = picture?.imageHeight ?? 1;
    return Boolean(width >= 10000 && width / height === 2);
  }, [picture]);

  const shouldShowCompare = shouldShowExpand && (!!prevInspectionDetails || !!previousDamageInfo);

  return (
    <ThumbnailContainer aspectRatio={aspectRatio}>
      {allowZoom ? (
        <>
          <Image
            isNewFullSizeImageViewer={false}
            isThumbnail={true}
            target={target}
            image={image}
            homography={homography}
            observations={(observations ?? []).map(o => ({
              ...o,
              selected: shouldAutoFocus && o.id === highestSeverityObservation?.id,
            }))}
            setObservation={handleClickObservation}
            primaryIndicator={primaryIndicator}
            thumbnailHeaderAttributes={
              shouldShowSeverity || shouldShowDistance ? (
                <AttributesContainerZoomable>
                  {shouldShowSeverity && (!!highestSeverity || isCritical) && (
                    <SeverityAndCriticalIndicator
                      value={highestSeverity}
                      isCritical={isCritical}
                      margin={'0 0.5rem 0 0'}
                      criticalLevel={CriticalLevel.Observation}
                    />
                  )}
                  {shouldShowDistance && (
                    <DistanceTag data-testid="distance-tag">
                      {isFinite(radialDistance ?? height) ? (
                        `${nf.format(radialDistance ?? height)}`
                      ) : (
                        <em>{t('images.na', 'N/A')}</em>
                      )}{' '}
                    </DistanceTag>
                  )}
                </AttributesContainerZoomable>
              ) : undefined
            }
          />
          {shouldShowCompare && (
            <CompareButton
              $isInCarousel={$isInCarousel}
              disabled={compareInspectionPicturesLoading}
              title={
                compareInspectionPicturesLoading ? undefined : t('components.compare', 'Compare')
              }
              icon={
                compareInspectionPicturesLoading ? <LoadingOutlined spin /> : <CompareDamagesIcon />
              }
              onClick={() => {
                if (!compareInspectionPicturesLoading) {
                  handleCompareNavigation?.({
                    previousDamageInfo,
                    previousInspectionInfo: prevInspectionDetails,
                  });
                }
              }}
            />
          )}
          {shouldShowExpand && (
            <ModalLink to={picturePath}>
              <ExpandButton
                icon={<FullscreenOutlined />}
                $isInCarousel={$isInCarousel}
                title={t('common.expand', 'Expand')}
              />
            </ModalLink>
          )}
          {shouldExpandToInspectionMap && mapPath && (
            <Link to={mapPath}>
              <MapButton
                icon={<EnvironmentOutlined />}
                $isInCarousel={$isInCarousel}
                title={t('inspection.map', 'Map')}
                aria-label={t('inspection.map', 'Map')}
              />
            </Link>
          )}
        </>
      ) : (
        <>
          {imageLoaded && shouldExpandToInspectionMap && mapPath && (
            <Link
              to={mapPath}
              onClick={event => {
                event.stopPropagation();
              }}
            >
              <Button
                icon={<EnvironmentOutlined />}
                title={t('inspection.map', 'Map')}
                aria-label={t('inspection.map', 'Map')}
                style={{
                  bottom: $isInCarousel ? 'calc(24px + 0.5rem)' : '0.5rem',
                  left: '3rem',
                  position: 'absolute',
                  zIndex: 2,
                }}
              />
            </Link>
          )}
          <ModalLink style={{ height: '100%' }} to={picturePath}>
            <RotationContainer
              degrees={shouldRotate ? componentRotationDegrees : 0}
              data-private="logrocket-hide"
            >
              {scrollParent && (
                <LazyLoadContainer
                  height={imageRect?.height}
                  placeholder={<Spinner />}
                  scrollContainer={scrollParentSelector}
                >
                  {!imageLoaded && <Spinner />}
                  <ThumbnailImage
                    ref={imageRef}
                    scaleFilter={scaleFilter}
                    alt={filename ?? t('images.pictureAlt', 'Picture {{id}}', { id: picture.id })}
                    onLoad={handleLoad}
                    src={
                      scaleFilter === 1
                        ? fullSizeImageWithoutEXIF || (thumbnailUrl as string)
                        : (thumbnailUrl as string) || fullSizeImageWithoutEXIF
                    }
                  />

                  {!!scale && (
                    <StyledSvg viewBox={`0 0 1 ${scale}`}>
                      {observations?.map(({ id, coordinates, attrs }) => (
                        <DamageTooltip
                          key={id}
                          id={id}
                          isHorizonDamage={true}
                          onClick={() => {
                            handleClickObservation(id);
                          }}
                        >
                          <g>
                            {coordinates && (
                              <StyledPolygon
                                data-testid="damage-polygon"
                                pointerEvents="visible"
                                points={coordinates.map((xy: any) => xy.join(',')).join(' ')}
                                severity={attrs?.['Severity']}
                              />
                            )}
                          </g>
                        </DamageTooltip>
                      ))}
                    </StyledSvg>
                  )}
                </LazyLoadContainer>
              )}
            </RotationContainer>
            {imageLoaded && (
              <ExtraContentContainer
                height={imageRect?.height ? `${imageRect.height}px` : '100%'}
                width={imageRect?.width ? `${imageRect.width}px` : '100%'}
              >
                <ButtonContainer $isInCarousel={$isInCarousel}>
                  <ButtonGroup>
                    {shouldShowExpand && (
                      <Button icon={<FullscreenOutlined />} title={t('common.expand', 'Expand')} />
                    )}
                  </ButtonGroup>
                  {shouldShowReviewedToggle && (
                    <div onClick={e => e.stopPropagation()}>
                      <ReviewedToggle
                        $small={!!(imageRect?.width && imageRect.width < 236)}
                        options={[
                          {
                            label:
                              reviewed && reviewedStatusLoading ? (
                                <Spin indicator={<LoadingOutlined spin />} />
                              ) : (
                                t('inspection.unreviewed', 'Unreviewed')
                              ),
                            value: false,
                          },
                          {
                            label:
                              !reviewed && reviewedStatusLoading ? (
                                <Spin indicator={<LoadingOutlined spin />} />
                              ) : (
                                t('inspection.reviewed', 'Reviewed')
                              ),
                            value: true,
                          },
                        ]}
                        onChange={({ target }: RadioChangeEvent) => {
                          setReviewedStatusLoading(true);
                          onToggleReviewed(picture, target.value);
                        }}
                        value={reviewed}
                        optionType="button"
                        buttonStyle="solid"
                      />
                    </div>
                  )}
                </ButtonContainer>

                <AttributesContainer>
                  {is360Picture && has360ViewerReleaseToggle && (
                    <span style={{ marginRight: 8 }} title={t('images.view360', '360 View')}>
                      <ThreeDimensionalIcon size={21} verticalAlign="middle" />
                    </span>
                  )}
                  {shouldShowSeverity && (!!highestSeverity || isCritical) && (
                    <SeverityAndCriticalIndicator
                      value={highestSeverity}
                      isCritical={isCritical}
                      margin={'0 0.5rem 0 0'}
                      criticalLevel={CriticalLevel.Image}
                    />
                  )}
                  {shouldShowDistance && (
                    <DistanceTag data-testid="distance-tag">
                      {isFinite(radialDistance ?? height) ? (
                        `${nf.format(radialDistance ?? height)}`
                      ) : (
                        <em>{t('images.na', 'N/A')}</em>
                      )}{' '}
                    </DistanceTag>
                  )}
                </AttributesContainer>
              </ExtraContentContainer>
            )}
          </ModalLink>
        </>
      )}
    </ThumbnailContainer>
  );
};
